import { toApiUrl } from "@libs/apiClient";

/**
 * LLM 자동 태깅 API
 * 백엔드 POST /api/v1/image/analyze 엔드포인트를 호출 (Gemini 전용)
 *
 * LLM 담당: 스타일, 실루엣/핏, 큐레이션 (3개 속성)
 * 분류 모델 담당: 타입, 색상, 패턴 (llmTagApi에서 처리하지 않음)
 */

export interface LLMTagResult {
  type: string[];
  style: string[];
  fit: string[];
  occasion: string[];
  confidence: { type: number; style: number; fit: number; occasion: number };
  reasoning?: string;
}

/**
 * 백엔드 POST /api/v1/image/analyze 호출 (Gemini)
 * imageUrl을 전달하면 백엔드가 서버 측에서 이미지를 다운로드하여 분석
 * imageBase64를 전달하면 base64 데이터를 직접 분석
 */
const callBackendLLM = async (
  imageBase64: string | null,
  imageUrl?: string
): Promise<LLMTagResult> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  try {
    const body: Record<string, string> = {};
    if (imageBase64) body.image_base64 = imageBase64;
    if (imageUrl) body.image_url = imageUrl;

    const response = await fetch(toApiUrl("/api/v1/image/analyze"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`LLM API error: ${response.status} - ${errText}`);
    }

    const json = await response.json();
    const data = json.data?.result || json;

    return {
      type: data.type || [],
      style: data.style || [],
      fit: data.fit || data.silhouette || [],
      occasion: data.occasion || data.curation || [],
      confidence: data.confidence || { type: 0.9, style: 0.9, fit: 0.9, occasion: 0.9 },
      reasoning: data.reasoning || "Gemini 분석 결과",
    };
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Gemini로 태그 분석
 * imageUrl만 전달하면 백엔드가 서버 측에서 이미지를 다운로드하여 분석 (CORS 우회)
 */
export const analyzeLLMTags = async (
  imageBase64: string | null,
  imageUrl?: string
): Promise<LLMTagResult> => {
  console.log("[LLM] 백엔드 Gemini로 태그 분석 시도");
  const result = await callBackendLLM(imageBase64, imageUrl);
  console.log("[LLM] Gemini 태그 분석 완료:", result);
  return result;
};
